import { snowflakeAt } from './contracts/snowflake.js';
import type {
  APIChannel,
  APIGatewayReady,
  APIMemberVerification,
  APIMessage,
  APIMessageReference,
  APIPartialEmoji,
} from './contracts/chat-types.js';
import {
  ChatApi,
  type CreateMessageBody,
  type LoginResponse,
  type MessagesQuery,
} from './api/chat-api.js';
import { ChatApiError } from './api/errors.js';
import { HttpClient, type HttpOptions, type UploadFile } from './api/http.js';
import {
  GatewayClient,
  type GatewayOptions,
  type GatewaySocketFactory,
  type GatewayTimers,
} from './gateway/gateway-client.js';
import { corpoDoAceite } from './interface/entrada.js';
import { DraftStore } from './rascunhos/drafts.js';
import { Outbox } from './rascunhos/outbox.js';
import { type KeyValueStorage, memoryStorage } from './rascunhos/storage.js';
import { type ChatStore, createChatStore } from './stores/chat-store.js';
import { selectIsUnread } from './stores/selectors.js';
import type { Snowflake } from './stores/state.js';

/** Mensagens por página, como o cliente do Discord. */
export const MESSAGES_PAGE = 50;
/** O Discord manda "digitando" de novo a cada ~8 s enquanto a pessoa escreve. */
const TYPING_RESEND_MS = 8_000;

export type ChatClientOptions = HttpOptions & {
  /** Como o cliente se apresenta no Identify. */
  properties: GatewayOptions['properties'];
  storage?: KeyValueStorage;
  socket?: GatewaySocketFactory;
  timers?: GatewayTimers;
  now?: () => number;
};

export type SendOptions = {
  content: string;
  /** Responder: a mensagem citada. */
  replyTo?: { messageId: Snowflake; mention?: boolean } | null;
  files?: UploadFile[];
  stickerIds?: Snowflake[];
  poll?: CreateMessageBody['poll'];
  /** `SUPPRESS_NOTIFICATIONS` (@silent) e `SUPPRESS_EMBEDS`. */
  flags?: number;
  allowedMentions?: CreateMessageBody['allowed_mentions'];
};

export type DispatchListener = (t: string, d: unknown) => void;

/**
 * O chat de uma conta: API, gateway, stores, rascunhos e fila de envio. A
 * interface usa a `store` (estado e seletores) e as ações daqui; o resto
 * (rotas menos usadas, telas de configuração) vai direto pela `api`.
 */
export class ChatClient {
  readonly http: HttpClient;
  readonly api: ChatApi;
  readonly store: ChatStore;
  private readonly storage: KeyValueStorage;
  private account: string | null = null;
  private draftStore: DraftStore;
  private outboxStore: Outbox;
  private gateway: GatewayClient | null = null;
  private readonly listeners = new Set<DispatchListener>();
  private readonly loading = new Map<string, Promise<APIMessage[]>>();
  private readonly lastTyping = new Map<Snowflake, number>();
  /**
   * O que foi pedido nas que falharam (arquivos, figurinhas, enquete…): a
   * pendente guarda só o que a tela mostra, e "Tentar de novo" manda tudo.
   */
  private readonly failedSends = new Map<string, SendOptions>();
  private readonly now: () => number;
  private flushing = false;
  private notesRequest: Promise<Record<Snowflake, string>> | null = null;
  /** Já tentou renovar o token depois de um 4004 (só uma vez por conexão). */
  private retriedAfterFatal = false;

  constructor(private readonly options: ChatClientOptions) {
    this.now = options.now ?? (() => Date.now());
    this.http = new HttpClient(options);
    this.api = new ChatApi(this.http);
    this.store = createChatStore(this.now);
    this.storage = options.storage ?? memoryStorage();
    this.draftStore = new DraftStore(this.storage);
    this.outboxStore = new Outbox(this.storage);
  }

  /** Rascunhos da conta (separados por pessoa: aparelho compartilhado). */
  get drafts(): DraftStore {
    return this.draftStore;
  }

  /** Fila de envio da conta (a de outra pessoa nunca sai com esta conta). */
  get outbox(): Outbox {
    return this.outboxStore;
  }

  /**
   * De quem é este cliente: rascunhos e fila de envio ficam guardados por
   * pessoa. A interface chama ao saber quem entrou (`users/@me`); o READY
   * confirma.
   */
  useAccount(userId: Snowflake): void {
    if (this.account === userId) return;
    this.account = userId;
    this.draftStore = new DraftStore(this.storage, userId);
    this.outboxStore = new Outbox(this.storage, userId);
  }

  // -------------------------------------------------------------------------
  // Sessão e conexão
  // -------------------------------------------------------------------------

  /** Entra com e-mail ou usuário (no celular, guarda os tokens). */
  async login(login: string, password: string) {
    return this.keepTokens(await this.api.login(login, password));
  }

  /** Define a senha pelo link de primeiro acesso e já entra. */
  async completeFirstAccess(token: string, password: string) {
    return this.keepTokens(await this.api.completeFirstAccess(token, password));
  }

  private async keepTokens(response: LoginResponse) {
    if (this.options.clientType === 'app' && response.accessToken) {
      await this.options.tokens?.set({
        accessToken: response.accessToken,
        refreshToken: response.refreshToken,
        accessTokenExpiresAt: response.accessTokenExpiresAt,
        refreshTokenExpiresAt: response.refreshTokenExpiresAt,
      });
    }
    return response;
  }

  /** Abre o gateway (o READY monta as stores) e traz a fila de envio guardada. */
  async connect(): Promise<void> {
    await this.restoreOutbox();
    if (this.gateway) {
      this.gateway.connect();
      return;
    }
    const { url } = await this.api.gateway();
    this.gateway = new GatewayClient({
      url,
      properties: this.options.properties,
      socket: this.options.socket,
      timers: this.options.timers,
      token:
        this.options.clientType === 'app'
          ? () => this.freshAccessToken()
          : undefined,
      onDispatch: (t, d) => {
        if (t === 'READY' || t === 'RESUMED') this.retriedAfterFatal = false;
        this.store.getState().dispatch(t, d);
        for (const listener of this.listeners) listener(t, d);
        if (t === 'READY') void this.ready(d as APIGatewayReady);
        if (t === 'RESUMED') void this.flushOutbox();
      },
      onStatus: (info) => this.store.getState().setConnection(info),
      onFatal: (code) => {
        if (code !== 4004) return;
        // No celular, o token pode ter vencido entre um Identify e outro:
        // renova uma vez e reconecta; só desiste se a renovação também falhar.
        if (this.options.clientType === 'app' && !this.retriedAfterFatal) {
          this.retriedAfterFatal = true;
          void this.http.refresh().then((ok) => {
            if (ok) this.gateway?.connect();
            else this.options.onSessionExpired?.();
          });
          return;
        }
        this.options.onSessionExpired?.();
      },
    });
    this.gateway.connect();
  }

  /**
   * O access token para o Identify do app: vencendo em menos de um minuto
   * (o celular pode ficar horas parado), renova antes.
   */
  private async freshAccessToken(): Promise<string | null> {
    const tokens = await this.options.tokens?.get();
    if (!tokens?.accessToken) return null;
    const expires = tokens.accessTokenExpiresAt
      ? Date.parse(tokens.accessTokenExpiresAt)
      : Number.NaN;
    if (Number.isFinite(expires) && expires - this.now() < 60_000) {
      await this.http.refresh();
    }
    return (await this.options.tokens?.get())?.accessToken ?? null;
  }

  /** READY: confirma a conta (fila e rascunhos dela) e manda o que ficou na fila. */
  private async ready(ready: APIGatewayReady): Promise<void> {
    if (this.account !== ready.user.id) {
      this.useAccount(ready.user.id);
      await this.restoreOutbox();
    }
    await this.flushOutbox();
  }

  disconnect(): void {
    this.gateway?.disconnect();
  }

  /** Rede voltou ou a pessoa pediu: tenta reconectar já. */
  reconnectNow(): void {
    this.gateway?.reconnectNow();
  }

  /** Sai da conta: encerra a sessão, fecha o gateway e esquece tudo. */
  /**
   * Troca de conta nesta aba sem sair pela API (o link de primeiro acesso
   * aberto com outra conta logada): fecha o gateway e limpa o estado; o
   * próximo `connect()` identifica do zero.
   */
  resetSession(): void {
    this.gateway?.disconnect();
    this.gateway = null;
    this.store.getState().reset();
  }

  async logout(): Promise<void> {
    const tokens =
      this.options.clientType === 'app'
        ? await this.options.tokens?.get()
        : null;
    try {
      await this.api.logout(tokens);
    } catch {
      // Sem rede: a sessão expira sozinha no servidor.
    }
    await this.options.tokens?.set(null);
    this.gateway?.disconnect();
    this.gateway = null;
    this.store.getState().reset();
    await this.outbox.clear();
  }

  /** Eventos do gateway, depois de aplicados à store. */
  subscribe(listener: DispatchListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  // -------------------------------------------------------------------------
  // Mensagens
  // -------------------------------------------------------------------------

  /**
   * Carrega uma página: sem nada, o fim do canal; `before`/`after` para
   * rolar; `around` para pular até uma mensagem. Pedidos iguais em
   * andamento são reaproveitados.
   */
  loadMessages(
    channelId: Snowflake,
    query: MessagesQuery = {},
  ): Promise<APIMessage[]> {
    const limit = query.limit ?? MESSAGES_PAGE;
    const key = `${channelId}:${query.before ?? ''}:${query.after ?? ''}:${query.around ?? ''}`;
    const running = this.loading.get(key);
    if (running) return running;
    const request = this.api
      .messages(channelId, { ...query, limit })
      .then((messages) => {
        this.store
          .getState()
          .messagesLoaded(channelId, messages, { ...query, limit });
        return messages;
      })
      .finally(() => this.loading.delete(key));
    this.loading.set(key, request);
    return request;
  }

  /** Mais antigas (rolar para cima). */
  loadOlder(channelId: Snowflake): Promise<APIMessage[]> {
    const first = this.store.getState().messages[channelId]?.ids[0];
    return this.loadMessages(channelId, first ? { before: first } : {});
  }

  /** Mais novas (depois de pular para uma mensagem antiga). */
  loadNewer(channelId: Snowflake): Promise<APIMessage[]> {
    const ids = this.store.getState().messages[channelId]?.ids ?? [];
    const last = ids[ids.length - 1];
    return this.loadMessages(channelId, last ? { after: last } : {});
  }

  /**
   * Envia com confirmação otimista, como o Discord: a mensagem aparece na
   * hora como "enviando"; sem conexão, vai para a fila e sai quando o
   * gateway voltar; recusada, fica marcada para tentar de novo.
   */
  async sendMessage(
    channelId: Snowflake,
    message: SendOptions,
  ): Promise<APIMessage | null> {
    const nonce = this.nonce();
    const reference: APIMessageReference | null = message.replyTo
      ? {
          type: 0,
          message_id: message.replyTo.messageId,
          channel_id: channelId,
          fail_if_not_exists: false,
        }
      : null;
    this.store.getState().addPending({
      nonce,
      channelId,
      content: message.content,
      createdAt: this.now(),
      status: 'sending',
      error: null,
      messageReference: reference,
      attachments: message.files?.map((file) => ({ name: file.name })),
    });
    return this.deliver(channelId, nonce, message, reference);
  }

  /**
   * Encaminha uma mensagem (como o Discord: `message_reference` tipo 1, a
   * original vira um "instantâneo" no destino) e, se veio, o comentário
   * logo depois.
   */
  async forwardMessage(
    channelId: Snowflake,
    message: APIMessage,
    comment?: string,
  ): Promise<APIMessage> {
    const origin = this.store.getState().channels[message.channel_id];
    const saved = await this.api.sendMessage(channelId, {
      message_reference: {
        type: 1,
        message_id: message.id,
        channel_id: message.channel_id,
        ...(origin?.guild_id ? { guild_id: origin.guild_id } : {}),
      },
    });
    this.store.getState().messageSaved(saved);
    if (comment?.trim()) {
      await this.sendMessage(channelId, { content: comment.trim() });
    }
    return saved;
  }

  /** Tenta de novo uma pendente que falhou. */
  async retryPending(channelId: Snowflake, nonce: string) {
    const pending = this.store
      .getState()
      .pending[channelId]?.find((item) => item.nonce === nonce);
    if (!pending) return null;
    this.store
      .getState()
      .updatePending(channelId, nonce, { status: 'sending', error: null });
    return this.deliver(
      channelId,
      nonce,
      this.failedSends.get(nonce) ?? { content: pending.content },
      pending.messageReference ?? null,
    );
  }

  async discardPending(channelId: Snowflake, nonce: string): Promise<void> {
    this.failedSends.delete(nonce);
    this.store.getState().removePending(channelId, nonce);
    await this.outbox.remove(nonce);
  }

  private async deliver(
    channelId: Snowflake,
    nonce: string,
    message: SendOptions,
    reference: APIMessageReference | null,
  ): Promise<APIMessage | null> {
    const store = this.store.getState();
    try {
      const saved = await this.api.sendMessage(
        channelId,
        {
          content: message.content,
          nonce,
          enforce_nonce: true,
          ...(reference
            ? {
                message_reference: {
                  message_id: reference.message_id as string,
                  fail_if_not_exists: false,
                },
              }
            : {}),
          ...(message.replyTo && message.replyTo.mention === false
            ? {
                allowed_mentions: {
                  replied_user: false,
                  parse: ['users', 'roles', 'everyone'],
                },
              }
            : message.allowedMentions
              ? { allowed_mentions: message.allowedMentions }
              : {}),
          ...(message.stickerIds?.length
            ? { sticker_ids: message.stickerIds }
            : {}),
          ...(message.poll ? { poll: message.poll } : {}),
          ...(message.flags ? { flags: message.flags } : {}),
        } as CreateMessageBody,
        message.files ?? [],
      );
      this.failedSends.delete(nonce);
      this.store.getState().messageSaved(saved);
      this.store.getState().removePending(channelId, nonce);
      await this.outbox.remove(nonce);
      // O rascunho é da interface (ela limpa ao enviar): aqui ele pode já
      // ser o texto seguinte, escrito enquanto esta saía.
      return saved;
    } catch (error) {
      if (error instanceof ChatApiError && error.isUnavailable) {
        if (message.files?.length) {
          this.failedSends.set(nonce, message);
          store.updatePending(channelId, nonce, {
            status: 'failed',
            error: error.message,
          });
        } else {
          store.updatePending(channelId, nonce, { status: 'queued' });
          await this.outbox.add({
            nonce,
            channelId,
            content: message.content,
            createdAt: this.now(),
            messageReference: reference,
          });
        }
        return null;
      }
      this.failedSends.set(nonce, message);
      store.updatePending(channelId, nonce, {
        status: 'failed',
        error: error instanceof Error ? error.message : String(error),
      });
      throw error;
    }
  }

  /** Manda o que ficou na fila, em ordem (para no primeiro erro de rede). */
  async flushOutbox(): Promise<void> {
    if (this.flushing) return;
    this.flushing = true;
    try {
      for (const queued of await this.outbox.load()) {
        this.store.getState().updatePending(queued.channelId, queued.nonce, {
          status: 'sending',
          error: null,
        });
        const sent = await this.deliver(
          queued.channelId,
          queued.nonce,
          { content: queued.content },
          queued.messageReference ?? null,
        ).catch(() => null);
        const still = this.outbox
          .list()
          .some((item) => item.nonce === queued.nonce);
        if (!sent && still) break;
      }
    } finally {
      this.flushing = false;
    }
  }

  private async restoreOutbox(): Promise<void> {
    const queued = await this.outbox.load();
    const store = this.store.getState();
    for (const item of queued) {
      store.addPending({
        nonce: item.nonce,
        channelId: item.channelId,
        content: item.content,
        createdAt: item.createdAt,
        status: 'queued',
        error: null,
        messageReference: item.messageReference ?? null,
      });
    }
  }

  async editMessage(
    channelId: Snowflake,
    messageId: Snowflake,
    content: string,
  ): Promise<APIMessage> {
    const saved = await this.api.editMessage(channelId, messageId, { content });
    this.store.getState().messageSaved(saved);
    return saved;
  }

  async deleteMessage(
    channelId: Snowflake,
    messageId: Snowflake,
    reason?: string,
  ): Promise<void> {
    await this.api.deleteMessage(channelId, messageId, { reason });
    this.store.getState().messageRemoved(channelId, messageId);
  }

  /** Reação otimista; se a API recusar, desfaz. */
  async react(
    channelId: Snowflake,
    messageId: Snowflake,
    emoji: APIPartialEmoji,
    burst = false,
  ): Promise<void> {
    const me = this.store.getState().me?.id;
    const event = {
      user_id: me,
      channel_id: channelId,
      message_id: messageId,
      emoji,
      burst,
      type: burst ? 1 : 0,
    };
    this.store.getState().dispatch('MESSAGE_REACTION_ADD', event);
    try {
      await this.api.addReaction(channelId, messageId, emoji, burst);
    } catch (error) {
      this.store.getState().dispatch('MESSAGE_REACTION_REMOVE', event);
      throw error;
    }
  }

  async unreact(
    channelId: Snowflake,
    messageId: Snowflake,
    emoji: APIPartialEmoji,
    burst = false,
  ): Promise<void> {
    const me = this.store.getState().me?.id;
    const event = {
      user_id: me,
      channel_id: channelId,
      message_id: messageId,
      emoji,
      burst,
      type: burst ? 1 : 0,
    };
    this.store.getState().dispatch('MESSAGE_REACTION_REMOVE', event);
    try {
      await this.api.removeReaction(channelId, messageId, emoji, { burst });
    } catch (error) {
      this.store.getState().dispatch('MESSAGE_REACTION_ADD', event);
      throw error;
    }
  }

  // -------------------------------------------------------------------------
  // Leitura e digitação
  // -------------------------------------------------------------------------

  /**
   * Marca o canal como lido até a última mensagem (chamar quando a pessoa
   * chega ao fim do canal, com espera). Nada a fazer se já está lido.
   */
  async ack(channelId: Snowflake): Promise<void> {
    const state = this.store.getState();
    const last = state.channels[channelId]?.last_message_id;
    if (
      !last ||
      (!selectIsUnread(state, channelId) &&
        !state.readStates[channelId]?.mention_count)
    ) {
      return;
    }
    state.setRead(channelId, last, 0);
    await this.api.ack(channelId, last);
  }

  /** "Marcar como não lida" a partir desta mensagem. */
  async markUnread(
    channelId: Snowflake,
    messageId: Snowflake,
    mentionCount = 0,
  ): Promise<void> {
    const before = (BigInt(messageId) - 1n).toString();
    this.store.getState().setRead(channelId, before, mentionCount);
    await this.api.ack(channelId, before, {
      manual: true,
      mention_count: mentionCount,
    });
  }

  /** "Digitando…": no máximo um aviso a cada 8 s por canal. */
  async startTyping(channelId: Snowflake): Promise<void> {
    const last = this.lastTyping.get(channelId) ?? 0;
    if (this.now() - last < TYPING_RESEND_MS) return;
    this.lastTyping.set(channelId, this.now());
    try {
      await this.api.typing(channelId);
    } catch {
      this.lastTyping.delete(channelId);
    }
  }

  /** Enviou a mensagem: o próximo aviso de digitação sai na hora. */
  stopTyping(channelId: Snowflake): void {
    this.lastTyping.delete(channelId);
  }

  // -------------------------------------------------------------------------
  // Outros
  // -------------------------------------------------------------------------

  /** Notas dos perfis: carrega uma vez (depois o `USER_NOTE_UPDATE` mantém). */
  loadNotes(): Promise<Record<Snowflake, string>> {
    const loaded = this.store.getState().notes;
    if (loaded) return Promise.resolve(loaded);
    this.notesRequest ??= this.api
      .notes()
      .then((notes) => {
        this.store.getState().notesLoaded(notes);
        return notes;
      })
      .finally(() => {
        this.notesRequest = null;
      });
    return this.notesRequest;
  }

  /** Grava a nota no perfil de alguém (otimista; vazia apaga). */
  async setNote(userId: Snowflake, note: string): Promise<void> {
    const before = this.store.getState().notes;
    this.store.getState().dispatch('USER_NOTE_UPDATE', {
      id: userId,
      note: note.trim(),
    });
    try {
      await this.api.setNote(userId, note);
    } catch (error) {
      if (before) this.store.getState().notesLoaded(before);
      throw error;
    }
  }

  /** Muda o status (vale em todas as sessões e fica guardado). */
  async setStatus(status: 'online' | 'idle' | 'dnd' | 'invisible') {
    const settings = await this.api.updateUserSettings({ status });
    this.store.getState().dispatch('USER_SETTINGS_UPDATE', { status });
    return settings;
  }

  /**
   * Parado há um tempo (sem mexer no computador): esta sessão fica "afk" e a
   * pessoa aparece Ausente quando todas as sessões estão paradas, sem mudar o
   * status escolhido (como o Discord). Voltou: `setAfk(false)`.
   */
  setAfk(afk: boolean): void {
    const status = this.store.getState().userSettings?.status ?? 'online';
    this.gateway?.updatePresence({
      status: afk ? 'idle' : status,
      since: afk ? this.now() : null,
      activities: [],
      afk,
    });
  }

  /**
   * Aceita as regras do servidor (o "Membership Screening" do Discord). A API
   * tira o pendente e avisa pelo `GUILD_MEMBER_UPDATE`; aqui já vale na hora,
   * sem esperar o gateway (o compositor libera no clique).
   */
  async acceptRules(
    guildId: Snowflake,
    verification: APIMemberVerification,
  ): Promise<void> {
    await this.api.acceptRules(guildId, corpoDoAceite(verification));
    const state = this.store.getState();
    const me = state.me;
    const member = me ? state.members[guildId]?.[me.id] : undefined;
    if (!me || !member?.pending) return;
    const { user_id: _userId, ...fields } = member;
    state.dispatch('GUILD_MEMBER_UPDATE', {
      ...fields,
      guild_id: guildId,
      user: state.users[me.id] ?? me,
      pending: false,
    });
  }

  /** Abre (ou acha) a DM com a pessoa. */
  async openDm(userId: Snowflake): Promise<APIChannel> {
    const channel = await this.api.openPrivateChannel({ recipient_id: userId });
    this.store.getState().upsertChannel(channel);
    return channel;
  }

  /** Nonce como o do Discord: um snowflake do instante (cabe nos 25 caracteres). */
  private nonce(): string {
    return snowflakeAt(this.now(), 0, 0, Math.floor(Math.random() * 4096));
  }
}
